import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Holds the filters a list page currently has applied.
 *
 * Two problems this solves.
 *
 * 1. Every list page has two ways of loading its rows - `fetchData(filters)`
 *    for a filtered load, and its own redux action for the plain one. Applying
 *    a filter used the first; every action afterwards (a toggle, a bulk update,
 *    closing a modal, a realtime invalidation) used the second, so the table
 *    quietly went back to showing everything while the panel still said a
 *    filter was on. Now every reload asks what is applied and gets it.
 *
 * 2. Opening a student and coming back threw the filter away. The filters are
 *    now written onto the list's own history entry, so browser Back (and the
 *    page's own back arrow, which is `navigate(-1)`) returns to the same
 *    filtered view - while picking the section again from the sidebar pushes a
 *    fresh entry, which carries no filters and so starts clean. That is the
 *    browser's own notion of "where I was", rather than a rule we invent.
 *
 * `getFilters()` is deliberately a ref read rather than the state value: the
 * pages hold their reload in a `useCallback` whose deps do not list it, so a
 * closure captured on mount would otherwise keep reporting "no filters"
 * forever - which is the exact bug this hook exists to fix.
 *
 * @param {(filters: Array) => void} loadFiltered  called when something is applied
 * @param {() => void} loadAll                     called when nothing is applied
 */
export const useListFilters = (loadFiltered, loadAll) => {
  const location = useLocation();
  const navigate = useNavigate();

  const remembered = Array.isArray(location.state?.listFilters)
    ? location.state.listFilters
    : [];

  const [activeFilters, setActiveFilters] = useState(remembered);
  const filtersRef = useRef(remembered);

  /** What is applied right now, safe to call from a stale closure. */
  const getFilters = useCallback(() => filtersRef.current, []);

  /** Re-run the current view. Call this after any action that changes rows. */
  const refresh = useCallback(
    (filters) => {
      const current = filters === undefined ? filtersRef.current : filters;
      if (current && current.length > 0) {
        loadFiltered(current);
      } else {
        loadAll();
      }
    },
    [loadFiltered, loadAll]
  );

  /** What the filter panel's Apply / Reset calls. */
  const applyFilters = useCallback(
    (filters) => {
      const next = filters || [];
      filtersRef.current = next;
      setActiveFilters(next);

      // Replace, not push: the user is still on the same page, so this must not
      // add a history entry they would have to press Back through.
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...(location.state || {}), listFilters: next },
      });

      refresh(next);
    },
    [refresh, navigate, location.pathname, location.search, location.state]
  );

  const clearFilters = useCallback(() => {
    filtersRef.current = [];
    setActiveFilters([]);
    loadAll();
  }, [loadAll]);

  return {
    activeFilters,
    getFilters,
    hasFilters: activeFilters.length > 0,
    applyFilters,
    clearFilters,
    refresh,
  };
};

export default useListFilters;
